/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import session from '../../../../share/session';
import config from '../../../../share/config';
import error from '../../../../common/nodejs/errors';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',
  EXECUTING: 'executing',
  SUCCESS: 'success',
  ERROR: 'error',
  FAILED: 'failed'
};

export default {
  name: 'requirement099',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      isDisabled: false,
      modal_requirement_099: false
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId,
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.START:
        case PHASE.EXECUTING:
          return 'secondary';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.ERROR:
        case PHASE.FAILED:
          return 'danger';
        default:
          return 'secondary';
      }
    }
  },

  mounted() {
    // 初期化処理を呼び出す
    this.init();
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    async init() {
      this.modal_requirement_099 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      // 終了処理エラー判定フラグ(初期状態はエラー判定無し)
      this.isErrorCheck = false;
    },

    //----- htmlイベント処理 -----------------------------------------------//

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_099 = false;
    },

    /** 画面を閉じる（終了処理を実行する）ボタンクリックイベント */
    async clickClose() {
      // 終了判定結果の取得
      if (await this.exitPostGet()) {
        this.closeWizard();
      }
    },

    /** 待機画面OKボタンクリックイベント */
    async clickExecute() {
      // 本遷移処理以降は終了処理でエラー判定を行う
      this.isErrorCheck = true;
      // 実行監視画面を表示
      this.showExecuting();
      // ルーチン実行処理を呼び出す
      await this.executePostGet();
    },

    //----- PHASE操作処理 -----------------------------------------------//

    /** 実行監視表示処理 */
    showExecuting() {
      this.phase = PHASE.EXECUTING;
    },

    /** 停止表示処理 */
    showSuccess() {
      this.phase = PHASE.SUCCESS;
    },

    /** エラー表示処理 */
    showError() {
      this.phase = PHASE.ERROR;
    },

    /** 共通エラー表示処理 */
    showFailed() {
      this.phase = PHASE.FAILED;
    },

    //----- バックエンドAPI実行処理 -----------------------------------------------//

    /**
     * ルーチン実行処理
     */
    async executePostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          // レスポンスが取得できた場合
          if (response.data.status === config.STATUS_WSP_COMPLETED) {
            // [成功]
            // 停止画面に遷移
            this.showSuccess();
          } else {
            // [成功以外]
            switch (response.data.async_code) {
              case error.WSP_EPS_INFO_CLEAR_ERROR:
                // [ルーチン実行: 異常終了]
                // [ルーチン結果確認: 0x0003]
                // エラー画面に遷移
                this.showError();
                break;
              default:
                // [上記以外のエラー]
                // 共通エラー画面に遷移
                this.showUnexpectedError();
                break;
            }
          }
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * 終了処理
     * @return 終了判定フラグ
     */
    async exitPostGet() {
      try {
        this.isLoading = true;
        this.isDisabled = true;

        // バックエンドAPI POST,GET
        const token = session.getToken();
        const response = await individualWorkSupportUtil.pollingWspApi(
          this,
          this.netAppId,
          this.systemId,
          this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる
          (errorType) => { errorType; if (this.isErrorCheck) this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (this.isErrorCheck) {
          // 終了処理のエラー判定を行う場合
          if (response) {
            // レスポンスが取得できた場合
            if (response.data.status === config.STATUS_WSP_COMPLETED) {
              // [成功]
              return true;
            } else {
              // [成功以外]
              // 共通エラー画面に遷移
              this.showUnexpectedError();
              return false;
            }
          } else {
            // レスポンスが取得できない場合、コールバックでエラー画面に遷移
            return false;
          }
        } else {
          return true;
        }
      } finally {
        this.isLoading = false;
        this.isDisabled = false;
      }
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; if (this.isErrorCheck) this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 共通エラー画面では終了処理でエラー判定を行わない
      this.isErrorCheck = false;
      // 共通エラー画面に遷移
      this.showFailed();
    },

  }
};